"use client";

import { motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  createHeroEntranceVariants,
  type HeroEntranceConfig,
} from "@/lib/hero-entrance-motion";

type DashboardAnimatedCardProps = {
  children: ReactNode;
  config: HeroEntranceConfig;
  className?: string;
  trigger?: "mount" | "inView";
  active?: boolean;
  finalScale?: number;
  settled?: boolean;
  settleAfterEntrance?: boolean;
  delay?: number;
  viewportAmount?: number;
};

export function DashboardAnimatedCard({
  children,
  config,
  className = "",
  trigger = "mount",
  active,
  finalScale,
  settled,
  settleAfterEntrance = false,
  delay = 0,
  viewportAmount = 0.35,
}: DashboardAnimatedCardProps) {
  const reducedMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: viewportAmount });
  const [entrancePhase, setEntrancePhase] = useState<"hidden" | "visible" | "settled">("hidden");
  const withSettled = settleAfterEntrance || settled !== undefined;
  const variants = createHeroEntranceVariants(!!reducedMotion, finalScale, withSettled, delay);
  const shouldAnimate = active !== undefined ? active : trigger === "inView" ? isInView : true;

  useEffect(() => {
    if (!settleAfterEntrance) return;
    if (shouldAnimate && entrancePhase === "hidden") {
      setEntrancePhase("visible");
    }
  }, [shouldAnimate, settleAfterEntrance, entrancePhase]);

  const motionProps = settleAfterEntrance
    ? {
        initial: "hidden" as const,
        animate: entrancePhase,
        onAnimationComplete: () => {
          setEntrancePhase((phase) => (phase === "visible" ? "settled" : phase));
        },
      }
    : active !== undefined
      ? {
          initial: "hidden" as const,
          animate: settled ? "settled" : active ? "visible" : "hidden",
        }
      : trigger === "inView"
        ? {
            initial: "hidden" as const,
            whileInView: "visible" as const,
            viewport: { once: true, amount: viewportAmount },
          }
        : {
            initial: "hidden" as const,
            animate: "visible" as const,
          };

  return (
    <motion.div
      ref={active === undefined && trigger === "inView" ? ref : undefined}
      className={className}
      custom={config}
      variants={variants}
      style={{ transformStyle: "preserve-3d", transformOrigin: "50% 100%" }}
      {...motionProps}
    >
      {children}
    </motion.div>
  );
}
